import { useEffect } from 'react';
import { createPortal } from 'react-dom';

const CloseIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
    <path
      d="M5 5l10 10M15 5L5 15"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const WIDTHS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
};

function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'lg',
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-ink-950/75 backdrop-blur-md animate-fade-up"
        onClick={onClose}
      />
      <div
        className={`glass-panel relative z-10 flex max-h-[92vh] w-full ${WIDTHS[size]} flex-col overflow-hidden rounded-t-3xl sm:rounded-3xl animate-fade-up`}
      >
        {(title || onClose) && (
          <header className="flex items-start justify-between gap-4 border-b border-white/5 px-6 py-5 sm:px-8">
            <div className="space-y-1.5">
              {title && (
                <h2
                  id="modal-title"
                  className="font-serif text-2xl tracking-tight"
                >
                  <span className="italic text-gradient">{title}</span>
                </h2>
              )}
              {description && (
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-white/45">
                  {description}
                </p>
              )}
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white/55 transition hover:bg-white/10 hover:text-white"
              >
                <CloseIcon />
              </button>
            )}
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-6 py-5 sm:px-8">
          {children}
        </div>
        {footer && (
          <footer className="flex flex-col-reverse gap-2 border-t border-white/5 bg-white/[0.02] px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-8">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;